import React from 'react';
import Layout from '@/components/Layout/Layout';
import { Helmet } from 'react-helmet-async';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, FileText, CheckCircle, AlertTriangle, Camera, Clipboard } from 'lucide-react';
import { Link } from 'react-router-dom';

const WorkingWithAdjustersPage = () => {
  return (
    <Layout>
      <Helmet>
        <title>Working with Insurance Adjusters | Carolina Trophy Roofs</title>
        <meta name="description" content="Professional guidance on working with insurance adjusters for roof damage claims. Learn what to expect and how Carolina Trophy Roofs supports you as your Contractor of Record." />
        <meta name="keywords" content="insurance adjuster, roof damage claim, contractor of record, adjuster meeting, North Carolina insurance" />
        <link rel="canonical" href="https://carolinatrophyroofs.com/resources/working-with-adjusters" />
      </Helmet>

      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <Link to="/resources" className="text-primary hover:underline">Resources</Link>
            <span className="mx-2">→</span>
            <span className="text-muted-foreground">Working with Insurance Adjusters</span>
          </nav>

          {/* Header */}
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              <Users className="mr-2 h-4 w-4" />
              Adjuster Relations
            </Badge>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Working with Insurance Adjusters
            </h1>
            <p className="text-xl text-gray-600">
              Professional guidance for successful adjuster interactions
            </p>
          </div>

          {/* Main Content */}
          <div className="prose prose-lg max-w-none">
            <div className="bg-green-50 border-l-4 border-green-500 p-6 mb-8">
              <h2 className="text-xl font-semibold text-green-800 mb-2">We're Here to Support You</h2>
              <p className="text-green-700">
                As your <strong>Contractor of Record</strong>, Carolina Trophy Roofs will be present 
                during adjuster meetings to provide technical expertise, present professional documentation, 
                and ensure all damage is properly identified and understood.
              </p>
            </div>

            <h2 className="text-2xl font-bold mb-6">Understanding Insurance Adjusters</h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-4">Staff Adjusters</h3>
                <p className="text-gray-600 mb-3">
                  Employees of the insurance company who handle claims directly for their employer.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Characteristics:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• Company employees with benefits</li>
                    <li>• May have company-specific protocols</li>
                    <li>• Usually more familiar with company policies</li>
                    <li>• May have different authority levels</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-4">Independent Adjusters</h3>
                <p className="text-gray-600 mb-3">
                  Third-party adjusters contracted by insurance companies to handle claims.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Characteristics:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• Work for multiple insurance companies</li>
                    <li>• Often handle catastrophic events</li>
                    <li>• May have broader industry experience</li>
                    <li>• Paid per claim processed</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4">The Adjuster Meeting Process</h2>
            
            <div className="space-y-6 mb-8">
              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-start mb-4">
                  <Clipboard className="h-6 w-6 text-primary mr-4 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Pre-Meeting Preparation</h3>
                    <p className="text-gray-600 mb-3">
                      Before the adjuster arrives, we prepare comprehensive documentation and organize 
                      all evidence of damage to present a clear, professional case.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Our Preparation Includes:</h4>
                      <ul className="text-sm space-y-1">
                        <li>• Complete photo and video documentation</li>
                        <li>• Detailed measurements and assessments</li>
                        <li>• Research on weather events and dates</li>
                        <li>• Preliminary damage estimate</li>
                        <li>• Code compliance evaluation</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-start mb-4">
                  <Camera className="h-6 w-6 text-primary mr-4 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">During the Inspection</h3>
                    <p className="text-gray-600 mb-3">
                      We guide the adjuster through the property, pointing out all areas of damage 
                      and providing technical context for their findings.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Our Role During Inspection:</h4>
                      <ul className="text-sm space-y-1">
                        <li>• Present organized damage documentation</li>
                        <li>• Point out areas of concern professionally</li>
                        <li>• Explain technical aspects of damage</li>
                        <li>• Discuss proper repair methodologies</li>
                        <li>• Answer construction-related questions</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-start mb-4">
                  <FileText className="h-6 w-6 text-primary mr-4 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Post-Inspection Follow-up</h3>
                    <p className="text-gray-600 mb-3">
                      After the adjuster completes their inspection, we review their findings and 
                      provide additional documentation if needed.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Follow-up Actions:</h4>
                      <ul className="text-sm space-y-1">
                        <li>• Review adjuster's initial findings</li>
                        <li>• Provide additional documentation if requested</li>
                        <li>• Submit detailed construction estimate</li>
                        <li>• Address any questions or concerns</li>
                        <li>• Coordinate supplemental inspections if needed</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4">Common Adjuster Concerns & Our Responses</h2>
            
            <div className="space-y-4 mb-8">
              <div className="bg-white p-6 rounded-lg border">
                <h4 className="font-semibold text-lg mb-2">Concern: "This looks like normal wear and tear"</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium mb-2">Our Professional Response:</h5>
                    <ul className="text-sm space-y-1">
                      <li>• Present storm timeline documentation</li>
                      <li>• Show before/after comparisons when available</li>
                      <li>• Explain impact patterns consistent with storm damage</li>
                      <li>• Provide technical analysis of damage characteristics</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium mb-2">Supporting Evidence:</h5>
                    <ul className="text-sm space-y-1">
                      <li>• Weather service reports</li>
                      <li>• Satellite and radar imagery</li>
                      <li>• Neighboring property damage</li>
                      <li>• Professional damage analysis</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <h4 className="font-semibold text-lg mb-2">Concern: "The estimate seems high"</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium mb-2">Our Professional Response:</h5>
                    <ul className="text-sm space-y-1">
                      <li>• Provide detailed line-item breakdown</li>
                      <li>• Reference local labor and material costs</li>
                      <li>• Explain code upgrade requirements</li>
                      <li>• Show manufacturer specifications</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium mb-2">Documentation Provided:</h5>
                    <ul className="text-sm space-y-1">
                      <li>• Current material pricing</li>
                      <li>• Labor rate justification</li>
                      <li>• Code compliance requirements</li>
                      <li>• Quality specification details</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <h4 className="font-semibold text-lg mb-2">Concern: "We don't see this damage"</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium mb-2">Our Professional Response:</h5>
                    <ul className="text-sm space-y-1">
                      <li>• Provide clear photographic evidence</li>
                      <li>• Use professional equipment for documentation</li>
                      <li>• Explain technical aspects of damage</li>
                      <li>• Offer additional inspection opportunities</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium mb-2">Additional Tools:</h5>
                    <ul className="text-sm space-y-1">
                      <li>• High-resolution photography</li>
                      <li>• Drone inspection capabilities</li>
                      <li>• Moisture detection equipment</li>
                      <li>• Detailed measurement tools</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8">
              <h3 className="text-lg font-semibold text-yellow-800 mb-3">
                <AlertTriangle className="inline h-5 w-5 mr-2" />
                When Good Faith Efforts Aren't Enough
              </h3>
              <p className="text-yellow-700 mb-3">
                If an insurance carrier fails to act in good faith despite our professional documentation 
                and evidence, we can introduce you to licensed public adjusters in our network who 
                specialize in taking disputed claims to the appraisal process.
              </p>
              <div className="bg-yellow-100 p-4 rounded-lg">
                <h4 className="font-medium text-yellow-800 mb-2">Appraisal Process Benefits:</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Independent third-party evaluation</li>
                  <li>• Licensed professional representation</li>
                  <li>• Binding resolution process</li>
                  <li>• Experienced in complex claims</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4">Best Practices for Adjuster Meetings</h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-green-50 p-6 rounded-lg">
                <CheckCircle className="h-8 w-8 text-green-500 mb-4" />
                <h3 className="text-lg font-semibold mb-3">Do's</h3>
                <ul className="space-y-2 text-sm">
                  <li>• Be present for the entire inspection</li>
                  <li>• Provide organized, professional documentation</li>
                  <li>• Ask questions if you don't understand something</li>
                  <li>• Take notes during the meeting</li>
                  <li>• Request copies of the adjuster's photos</li>
                  <li>• Be courteous and professional</li>
                  <li>• Follow up in writing when needed</li>
                </ul>
              </div>
              
              <div className="bg-red-50 p-6 rounded-lg">
                <AlertTriangle className="h-8 w-8 text-red-500 mb-4" />
                <h3 className="text-lg font-semibold mb-3">Don'ts</h3>
                <ul className="space-y-2 text-sm">
                  <li>• Don't sign anything without understanding it</li>
                  <li>• Don't accept the first estimate without review</li>
                  <li>• Don't let them rush through the inspection</li>
                  <li>• Don't be confrontational or argumentative</li>
                  <li>• Don't agree to quick settlements</li>
                  <li>• Don't provide recorded statements alone</li>
                  <li>• Don't accept lowball offers immediately</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 p-6 rounded-lg mb-8">
              <h3 className="text-lg font-semibold text-blue-800 mb-3">Remember: We're Your Advocate</h3>
              <p className="text-blue-700">
                While you maintain direct communication with your insurance carrier, Carolina Trophy Roofs 
                serves as your technical expert and documentation specialist. Our goal is to ensure 
                your property is properly restored to pre-damage conditions using quality materials 
                and professional workmanship.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg mb-8">
              <h3 className="text-lg font-semibold mb-4">Authoritative Sources & References</h3>
              <p className="text-sm text-gray-600 mb-4">
                The following authoritative sources provide additional information about insurance adjusters and the claims process:
              </p>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h4 className="font-medium mb-2">Industry Organizations:</h4>
                  <ul className="space-y-1">
                    <li>
                      <a href="https://www.naic.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        National Association of Insurance Commissioners (NAIC)
                      </a>
                    </li>
                    <li>
                      <a href="https://www.iii.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Insurance Information Institute
                      </a>
                    </li>
                    <li>
                      <a href="https://www.cpcu.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        American Institute for CPCU
                      </a>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Government Resources:</h4>
                  <ul className="space-y-1">
                    <li>
                      <a href="https://www.ncdoi.gov/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        NC Department of Insurance
                      </a>
                    </li>
                    <li>
                      <a href="https://www.fema.gov/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Federal Emergency Management Agency (FEMA)
                      </a>
                    </li>
                    <li>
                      <a href="https://www.weather.gov/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        National Weather Service
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="text-center mt-12">
              <h2 className="text-2xl font-bold mb-4">Ready for Your Adjuster Meeting?</h2>
              <p className="text-lg text-muted-foreground mb-6">
                Get professional support and expert documentation for your insurance claim.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg">
                  <Link to="/contact">Schedule Assessment</Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <Link to="/resources">
                    <FileText className="mr-2 h-5 w-5" />
                    More Resources
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default WorkingWithAdjustersPage;
